/**
 * SIM-SZENE LEIPZIG – GameWindow
 * Haupt-Container im American-Dream-Stil (1996).
 *
 * Layout (wie im Original):
 * - Oberer Bereich: Animierter Sternenhimmel + Ressourcen-Balken
 * - Mittlerer Bereich: Szenario-Bild und Text
 * - Unterer Bereich: Status-Zeile (Runde, Phasen/Pfad, Sound-Toggle, Leipzig)
 */
import { useState, useCallback } from 'preact/hooks';
import { ResourceBar } from './ResourceBar.jsx';
import { PhasePathIndicator } from './PhasePathIndicator.jsx';
import {
  getMuted,
  setMuted,
  getMusicMuted,
  setMusicMuted,
  getMusicVolume,
  setMusicVolume,
} from '../audio/sound.js';
import './GameWindow.css';

const VOLUME_MIN = 0;
const VOLUME_MAX = 1;
const VOLUME_STEP = 0.05;

export function GameWindow({ children, round }) {
  const [muted, setMutedState] = useState(() => getMuted());
  const [musicMuted, setMusicMutedState] = useState(() => getMusicMuted());
  const [musicVolume, setMusicVolumeState] = useState(() => getMusicVolume());

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
  };

  const toggleMusic = () => {
    const next = !musicMuted;
    setMusicMuted(next);
    setMusicMutedState(next);
  };

  const onMusicVolumeChange = useCallback((e) => {
    const val = parseFloat(e.target.value);
    setMusicVolume(val);
    setMusicVolumeState(val);
  }, []);

  return (
    <div className="game-window">
      <header className="game-header">
        <div className="stars-bg"></div>
        <ResourceBar />
      </header>

      <main className="game-main">
        {children}
      </main>

      <footer className="game-footer">
        <span className="footer-item">
          <span className="footer-icon">📅</span>
          Runde {round}
        </span>
        <span className="footer-item footer-phase">
          <PhasePathIndicator />
        </span>
        <span className="footer-item footer-audio">
          <button
            type="button"
            className="footer-item footer-sound-toggle"
            onClick={toggleMute}
            title={muted ? 'Sound einschalten' : 'Sound ausschalten'}
            aria-label={muted ? 'Sound einschalten' : 'Sound ausschalten'}
          >
            <span className="footer-icon" aria-hidden="true">
              {muted ? '🔇' : '🔊'}
            </span>
          </button>
          <button
            type="button"
            className={`footer-item footer-music-toggle${musicMuted ? ' footer-music-toggle--off' : ''}`}
            onClick={toggleMusic}
            title={musicMuted ? 'Musik einschalten' : 'Musik ausschalten'}
            aria-label={musicMuted ? 'Musik einschalten' : 'Musik ausschalten'}
          >
            <span className="footer-icon" aria-hidden="true">🎵</span>
          </button>
          <span className="footer-item footer-music-volume" title="Lautstärke Musik">
            <label className="footer-music-volume-label" htmlFor="music-volume">
              <span className="footer-icon" aria-hidden="true">🔈</span>
            </label>
            <input
              id="music-volume"
              type="range"
              min={VOLUME_MIN}
              max={VOLUME_MAX}
              step={VOLUME_STEP}
              value={musicVolume}
              onChange={onMusicVolumeChange}
              disabled={musicMuted}
              aria-label="Lautstärke Musik"
              className="footer-music-volume-slider"
            />
          </span>
        </span>
      </footer>
    </div>
  );
}
